// The launch waitlist behind the holding page (HoldingPage.jsx). Everything
// goes through server functions (20261013120000_waitlist_holding_page.sql):
// join_waitlist answers the same way for a new or existing address,
// confirm_waitlist records the double opt-in and returns the traveller's
// invitation code, and unsubscribe_waitlist removes a row by the private id
// in every email. Clients cannot read or write the table itself.
import { supabase } from './supabase';
import { trackEvent } from './analytics';

const INVITE_KEY = 'sacredmothr_waitlist_invite';
const CODE_RE = /^[A-HJ-NP-Z2-9]{6}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Same rule as the server, so most typos are caught before sending.
export function isEmail(value) {
  const v = String(value || '').trim();
  return v.length <= 254 && /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(v);
}

export const normaliseCode = (value) => String(value || '').trim().toUpperCase();
export const isInviteCode = (value) => CODE_RE.test(normaliseCode(value));
export const isToken = (value) => UUID_RE.test(String(value || ''));

// The links the page understands: ?invite=CODE (someone brought them),
// ?confirm=TOKEN and ?unsubscribe=ID (from the emails).
export function readWaitlistParams(search) {
  const params = new URLSearchParams(search || '');
  const invite = normaliseCode(params.get('invite'));
  const confirm = params.get('confirm');
  const unsubscribe = params.get('unsubscribe');
  return {
    invite: isInviteCode(invite) ? invite : null,
    confirm: isToken(confirm) ? confirm : null,
    unsubscribe: isToken(unsubscribe) ? unsubscribe : null,
  };
}

// The invitation a visitor arrived with, kept for the visit so it still
// counts if they wander the page before signing up.
export function rememberInvite(code) {
  try { if (code) sessionStorage.setItem(INVITE_KEY, code); } catch { /* storage blocked */ }
}
export function rememberedInvite() {
  try { const c = sessionStorage.getItem(INVITE_KEY); return isInviteCode(c) ? c : null; } catch { return null; }
}

export function inviteLink(code, origin = (typeof window !== 'undefined' ? window.location.origin : 'https://sacredmothr.com')) {
  return `${origin}/?invite=${encodeURIComponent(code)}`;
}

// Store buttons appear only once a listing exists (VITE_APP_STORE_URL,
// VITE_PLAY_STORE_URL in the build settings; public values, not secrets).
export function storeLinks(env = import.meta.env) {
  const ok = (u) => (typeof u === 'string' && /^https:\/\//.test(u.trim()) ? u.trim() : null);
  return { apple: ok(env?.VITE_APP_STORE_URL), google: ok(env?.VITE_PLAY_STORE_URL) };
}

// Plain-language error for a failed sign-up.
export function joinErrorText(error) {
  const msg = String(error?.message || error || '');
  if (msg.includes('waitlist_email')) return 'That email address looks incomplete. Check it and try again.';
  if (msg.includes('waitlist_busy')) return 'A lot of travellers are signing up right now. Please try again in a few minutes.';
  return 'We could not reach the ship just now. Check your connection and try again.';
}

export async function joinWaitlist(email, invite = null) {
  const { error } = await supabase.rpc('join_waitlist', { p_email: String(email || '').trim(), p_invite: invite || null });
  if (error) throw error;
}

// Returns { code, pass }: the traveller's invitation code and their First
// Light boarding pass (20261020120000_first_light.sql), or null for an
// unknown link.
export async function confirmWaitlist(token) {
  const { data, error } = await supabase.rpc('confirm_waitlist', { p_token: token });
  if (error) throw error;
  if (!data?.invite_code) return null;
  return { code: data.invite_code, pass: isToken(data.board_pass) ? data.board_pass : null };
}

// The boarding pass link: opened while signed in to the app, it claims First
// Light for an account with a different email (or Apple's Hide My Email).
export function boardingPassLink(pass, origin = (typeof window !== 'undefined' ? window.location.origin : 'https://sacredmothr.com')) {
  return `${origin}/?board=${encodeURIComponent(pass)}`;
}

export async function unsubscribeWaitlist(id) {
  const { error } = await supabase.rpc('unsubscribe_waitlist', { p_id: id });
  if (error) throw error;
}

// Anonymous steps for the funnel: shown, sound_on, signup, confirmed. One
// random id per visit, no email.
const RUN_KEY = 'sacredmothr_holding_run';
const sent = new Set();
export function trackHoldingStep(step) {
  if (sent.has(step)) return;
  sent.add(step);
  let run = null;
  try { run = sessionStorage.getItem(RUN_KEY); } catch { /* storage blocked */ }
  if (!run) {
    run = globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    try { sessionStorage.setItem(RUN_KEY, run); } catch { /* storage blocked */ }
  }
  trackEvent('holding_step', { step, run });
}
